import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export function PlanAccessRequests() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/plan-access/incoming").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  async function respond(id, approve) { await api.post(`/plan-access/${id}/respond`, { approve }); toast.success(approve ? "Planner can now see and add to your plan" : "Request declined"); load(); }
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[var(--gold)]/40 rounded-2xl p-4 space-y-2" data-testid="plan-access-requests">
      <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Your planners</p>
      {rows.map((r) => (
        <div key={r.access_id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span><b>{r.planner_name}</b> {r.status === "approved" ? "can view and add milestones to your plan" : "wants to work on your milestone plan with you"}</span>
          {r.status === "pending" ? (
            <span className="flex gap-2">
              <button onClick={() => respond(r.access_id, true)} className="btn-gold !py-1.5 !px-4 text-[11px]" data-testid={`plan-access-approve-${r.access_id}`}>Approve</button>
              <button onClick={() => respond(r.access_id, false)} className="btn-outline-gold !py-1.5 !px-4 text-[11px]" data-testid={`plan-access-decline-${r.access_id}`}>Decline</button>
            </span>
          ) : <button onClick={() => respond(r.access_id, false)} className="text-xs text-[var(--muted)] underline" data-testid={`plan-access-revoke-${r.access_id}`}>Remove access</button>}
        </div>
      ))}
    </div>
  );
}

export default function ClientPlanPage() {
  const { weddingId } = useParams();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ title: "", due_date: "" });
  const load = useCallback(() => api.get(`/planner/plans/${weddingId}`).then((r) => setD(r.data)).catch((e) => setD({ error: formatApiError(e) })), [weddingId]);
  useEffect(() => { load(); }, [load]);
  async function add(e) {
    e.preventDefault();
    try { await api.post(`/planner/plans/${weddingId}/milestones`, { ...f, due_date: f.due_date || null }); setF({ title: "", due_date: "" }); load(); toast.success("Milestone added to the couple's plan"); }
    catch (er) { toast.error(formatApiError(er)); }
  }
  async function toggle(t) { await api.patch(`/planner/plans/${weddingId}/tasks/${t.task_id}`, { completed: !t.completed }); load(); }
  if (!d) return <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />;
  if (d.error) return <p className="text-sm text-red-600" data-testid="client-plan-error">{d.error}</p>;
  const done = d.tasks.filter((t) => t.completed).length;
  return (
    <div className="max-w-3xl space-y-5" data-testid="client-plan-page">
      <div className="flex justify-between items-center">
        <Link to="/dashboard/contacts" className="text-sm text-[var(--muted)] flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Client book</Link>
        <Link to={`/dashboard/guest-messages?wedding=${weddingId}`} className="btn-outline-gold text-xs" data-testid="client-guest-messages">Guest messages</Link>
      </div>
      <div>
        <p className="font-script text-[var(--gold)] text-3xl">{d.wedding?.couple_names}</p>
        <h1 className="font-serif font-light text-4xl">Shared milestone plan</h1>
        <p className="text-sm text-[var(--muted)] mt-1">{done} of {d.tasks.length} done{d.wedding?.wedding_date && ` · wedding ${d.wedding.wedding_date}`}. The couple sees every change.</p>
        <div className="h-2 rounded-full bg-white mt-3 overflow-hidden"><div className="h-full bg-[var(--gold)] transition-[width] duration-700" style={{ width: `${d.tasks.length ? (done / d.tasks.length) * 100 : 0}%` }} /></div>
      </div>
      <form onSubmit={add} className="flex flex-wrap gap-2 bg-white border border-[var(--line)] rounded-2xl p-3" data-testid="client-plan-add">
        <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="New milestone, e.g. Final venue walkthrough" className="flex-1 min-w-[200px] bg-[var(--bg)] rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="client-plan-title" />
        <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="bg-[var(--bg)] rounded-xl px-3 py-2.5 text-sm" data-testid="client-plan-date" />
        <button className="btn-gold !py-2.5" data-testid="client-plan-submit"><Plus className="w-4 h-4" /> Add</button>
      </form>
      <div className="bg-white border border-[var(--line)] rounded-2xl divide-y divide-[var(--line)]">
        {d.tasks.length === 0 && <p className="p-6 text-sm text-[var(--muted)] text-center">No milestones yet. They appear once the couple sets a date, or add the first one above.</p>}
        {d.tasks.map((t) => (
          <button key={t.task_id} onClick={() => toggle(t)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg)]" data-testid={`client-plan-task-${t.task_id}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center ${t.completed ? "bg-[var(--gold)] border-[var(--gold)] text-white" : "border-[var(--line)]"}`}>{t.completed && <Check className="w-3.5 h-3.5" />}</span>
            <span className={`flex-1 text-sm ${t.completed ? "line-through text-[var(--muted)]" : ""}`}>{t.title}{t.created_by_name && <span className="text-[10px] text-[var(--gold)] ml-2">by {t.created_by_name}</span>}</span>
            <span className="text-xs text-[var(--muted)]">{t.milestone} · {t.due_date || "no date"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
